/**
 * A classe econômica do período e a compensação estimada (`OB-01`, ADR-066).
 *
 * Nos contratos paulistas novos cada passagem é **válida**, **inválida** ou **inválida
 * fraudulenta**, e a classe vira linha de cobrança contra o Estado. Esta parte da tela diz
 * quantas há de cada uma, quanto valem e quanto o poder concedente devolveria **se nada mais
 * fosse pago** — pela regra do regime de cada concessão. Onde o regime não traz regra, a
 * conta usa a paulista e diz, com o selo e a nota, que é simulação (decisão de 13/10).
 *
 * Cada número abre a fila do recorte que o produziu, com a mesma contagem (`AC14`), como o
 * resto da régua. A tela não calcula nada: a classe, a soma e a compensação vêm do servidor.
 */
import type {
  ClasseEconomica,
  ClasseEconomicaDoPeriodo,
  ParteDaClasse,
} from "../api/tipos";
import { APRESENTACAO_DA_CLASSE } from "../api/tipos";
import { Badge } from "../componentes/base";
import { numeroLongo, reais } from "../formato";

/** As quatro classes, na ordem do contrato, e a chave da parte de cada uma. */
const CLASSES: { classe: ClasseEconomica; chave: string }[] = [
  { classe: "VALIDA", chave: "validas" },
  { classe: "FRAUDULENTA", chave: "fraudulentas" },
  { classe: "INVALIDA", chave: "invalidas" },
  { classe: "EM_APURACAO", chave: "em_apuracao" },
];

/** As partes das válidas que a compensação lê — e, quando o período tem passagem sob o
 *  protocolo federal de notificação (`OB-08`), as com e as sem a prova (o servidor só as manda
 *  então). */
const DAS_VALIDAS = [
  "validas_nao_pagas",
  "validas_nunca_cobradas",
  "nao_pagas_com_prova",
  "nao_pagas_sem_prova",
];

export function ClasseEconomicaNaRegua({
  classe,
  aoAbrir,
}: {
  classe: ClasseEconomicaDoPeriodo;
  aoAbrir: (recorte: string) => void;
}) {
  const parte = (chave: string) => classe.partes.find((p) => p.chave === chave);
  const fontes = [...new Set(classe.por_concessao.map((c) => c.fonte))];
  const soma = (campo: "das_validas" | "das_fraudulentas" | "das_invalidas") =>
    classe.por_concessao.reduce((total, c) => total + c[campo], 0);

  return (
    <div className="grid gap-3 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <div className="grid content-start gap-2">
        <ul
          aria-label="Classes das passagens"
          className="grid grid-cols-2 gap-2 lg:grid-cols-4"
        >
          {CLASSES.map(({ classe: c, chave }) => {
            const p = parte(chave);
            return p ? (
              <li key={chave} className="min-w-0">
                <PorClasse parte={p} classe={c} aoAbrir={aoAbrir} />
              </li>
            ) : null;
          })}
        </ul>
        <ul aria-label="Das válidas" className="grid gap-1.5 sm:grid-cols-2">
          {DAS_VALIDAS.map((chave) => {
            const p = parte(chave);
            return p ? (
              <li key={chave} className="min-w-0">
                <button
                  type="button"
                  onClick={() => aoAbrir(p.recorte)}
                  title={p.nota ?? undefined}
                  className="grid w-full gap-0.5 rounded-cartao border border-linha bg-painel-2 px-2.5 py-1.5 text-left hover:bg-realce"
                >
                  <span className="text-[11px] text-tinta-3">{p.rotulo}</span>
                  <span className="flex items-baseline gap-2">
                    <b className="mono text-[14px] text-tinta">
                      {numeroLongo(p.quantidade)}
                    </b>
                    <span className="mono text-[11px] text-tinta-2">
                      {reais(p.valor_centavos)}
                    </span>
                  </span>
                  {p.nota && (
                    <span className="text-[10px] leading-snug text-tinta-3">
                      {p.nota}
                    </span>
                  )}
                </button>
              </li>
            ) : null;
          })}
        </ul>
      </div>

      <section
        aria-label="Compensação estimada"
        className="grid content-start gap-1.5 rounded-cartao border border-linha bg-painel-2 p-2.5"
      >
        <span className="flex flex-wrap items-center gap-1.5 text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
          Compensação estimada
          {classe.simulada && <Badge cor="amarelo">simulação</Badge>}
        </span>
        <b className="mono text-[26px] leading-none text-tinta">
          {reais(classe.compensacao_centavos)}
        </b>
        <span className="text-[11px] text-tinta-3">
          o que o poder concedente devolveria se nada mais fosse pago
        </span>
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11.5px]">
          <dt className="text-tinta-3">Das válidas não pagas</dt>
          <dd className="mono text-right text-tinta-2">
            {reais(soma("das_validas"))}
          </dd>
          <dt className="text-tinta-3">Das fraudulentas</dt>
          <dd className="mono text-right text-tinta-2">
            {reais(soma("das_fraudulentas"))}
          </dd>
          <dt className="text-tinta-3">Das inválidas</dt>
          <dd className="mono text-right text-tinta-2">
            {reais(soma("das_invalidas"))}
          </dd>
        </dl>
        {classe.por_concessao.length > 1 && (
          <ul
            aria-label="Compensação por concessão"
            className="grid gap-0.5 border-t border-linha-suave pt-1.5 text-[11px]"
          >
            {classe.por_concessao.map((c) => (
              <li
                key={`${c.concessao_id}-${c.regime}`}
                className="flex items-baseline gap-2"
              >
                <span className="min-w-0 flex-1 truncate text-tinta-2">
                  {c.concessao}
                  {c.simulada && (
                    <span className="text-amarelo"> · simulação</span>
                  )}
                </span>
                <span className="mono text-tinta-2">{reais(c.total)}</span>
              </li>
            ))}
          </ul>
        )}
        {classe.nota && (
          <p className="text-[10.5px] leading-snug text-tinta-3">
            {classe.nota}
          </p>
        )}
        {fontes.length > 0 && (
          <p className="text-[10px] leading-snug text-tinta-3">
            Regra: {fontes.join("; ")}.
          </p>
        )}
      </section>
    </div>
  );
}

function PorClasse({
  parte,
  classe,
  aoAbrir,
}: {
  parte: ParteDaClasse;
  classe: ClasseEconomica;
  aoAbrir: (recorte: string) => void;
}) {
  const apresentacao = APRESENTACAO_DA_CLASSE[classe];
  return (
    <button
      type="button"
      onClick={() => aoAbrir(parte.recorte)}
      title={parte.nota ?? undefined}
      className="grid h-full w-full content-start gap-1 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2 text-left hover:bg-realce"
    >
      <span>
        <Badge cor={apresentacao.cor}>{apresentacao.rotulo}</Badge>
      </span>
      <b className="mono text-[20px] leading-none text-tinta">
        {numeroLongo(parte.quantidade)}
      </b>
      <span className="mono text-[11px] text-tinta-2">
        {parte.valor_centavos == null
          ? "sem tarifa"
          : reais(parte.valor_centavos)}
      </span>
      {parte.nota && (
        <span className="line-clamp-3 text-[10px] leading-snug text-tinta-3">
          {parte.nota}
        </span>
      )}
    </button>
  );
}
